import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, toast } from '@eureca/ui';
import { LoaderCircleIcon, TriangleAlertIcon } from '@eureca/ui/icons';
import { useAuth } from '@/auth/use-auth';
import { AdminLayout } from '@/layout/AdminLayout';
import { assessmentsListQueryOptions, type AssessmentListEntry } from './assessments-query';
import { AssessmentMutationError, usePatchAssessment } from './assessments-mutations';
import { AssessmentPolicyDialog, type AssessmentPolicyValues } from './AssessmentPolicyDialog';

/**
 * "Testes" — a biblioteca de assessments como CONFIG DE PLATAFORMA (spec 003:
 * política de reuso cross-vaga; spec 005: mora no admin-app, sem contexto de
 * empresa). Duas seções por `kind`:
 *
 *   - **Padrão** (`preset_*`): instrumentos de fábrica. Conteúdo imutável no
 *     Core (409 `preset_immutable`) — só a política `{inheritable,
 *     inheritTtlMonths}` é editável (e só por admin).
 *   - **Personalizados** (`ad_hoc`): testes de empresa. Regra de produto: nunca
 *     herdam entre vagas (Core: 422 `adhoc_not_inheritable`) — sem controles.
 */

function isAdHoc(a: AssessmentListEntry): boolean {
  return a.kind === 'ad_hoc';
}

/**
 * "Reaproveita entre vagas: Sim/Não · Validade: N meses / padrão (6 meses)".
 * EU-750: `inheritTtlMonths: null` deixou de ser "sem prazo" — o Core aplica o
 * prazo PADRÃO (`ASSESSMENT_RESULT_VALIDITY_MONTHS` = 6). Toda resposta vence.
 */
function policyLabel(a: AssessmentListEntry): string {
  if (isAdHoc(a)) return 'Não reaproveitável (teste da empresa)';
  if (!a.inheritable) return 'Reaproveita entre vagas: Não';
  const validade =
    a.inheritTtlMonths !== null
      ? `${a.inheritTtlMonths} ${a.inheritTtlMonths === 1 ? 'mês' : 'meses'}`
      : 'padrão (6 meses)';
  return `Reaproveita entre vagas: Sim · Validade: ${validade}`;
}

export default function AssessmentsListPage() {
  const { isAdmin } = useAuth();
  const [editing, setEditing] = useState<AssessmentListEntry | null>(null);

  const listQuery = useQuery(assessmentsListQueryOptions());
  const patchAssessment = usePatchAssessment();

  const items = listQuery.data?.items ?? [];
  const presets = items.filter((a) => !isAdHoc(a));
  const adHoc = items.filter(isAdHoc);

  const isLoading = listQuery.isLoading;
  const isError = listQuery.isError;
  const isEmpty = !isLoading && !isError && items.length === 0;

  async function handleSavePolicy(values: AssessmentPolicyValues) {
    if (!editing) return;
    try {
      await patchAssessment.mutateAsync({
        id: editing.id,
        patch: { inheritable: values.inheritable, inheritTtlMonths: values.inheritTtlMonths },
      });
      toast('Política de reuso atualizada.', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      setEditing(null);
    } catch (err) {
      const status = err instanceof AssessmentMutationError ? err.status : 0;
      if (status === 409) {
        toast.error('Testes padrão são imutáveis: apenas a política de reuso pode ser alterada.');
      } else if (status === 422) {
        toast.error('Testes personalizados não podem ser reaproveitados entre vagas.');
      } else if (status === 401 || status === 403) {
        toast.error('Você não tem permissão para alterar a política deste teste.');
      } else if (status === 404) {
        toast.error('Teste não encontrado.');
        setEditing(null);
      } else {
        toast.error('Não foi possível salvar a política de reuso. Tente novamente.');
      }
    }
  }

  return (
    <AdminLayout activeNavId="assessments">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Testes</h1>
        <p className="text-sm text-muted-foreground">
          Configuração de plataforma: política de reaproveitamento dos testes entre vagas. Vale para
          todas as empresas.
          {!isAdmin ? ' Você está visualizando em modo leitura.' : ''}
        </p>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <StatusPanel
            icon={<LoaderCircleIcon className="size-7 animate-spin" />}
            title="Buscando testes..."
          />
        ) : isError ? (
          <StatusPanel
            icon={<TriangleAlertIcon className="size-7" />}
            title="Não foi possível carregar os testes."
          />
        ) : isEmpty ? (
          <StatusPanel
            icon={<TriangleAlertIcon className="size-7" />}
            title="Sem testes"
            description="No momento, ainda não há nenhum teste cadastrado."
          />
        ) : (
          <div className="flex flex-col gap-8">
            <AssessmentsSection
              heading="Padrão"
              description="Instrumentos da plataforma. O conteúdo é fixo — apenas a política de reuso entre vagas pode ser ajustada."
              emptyMessage="Nenhum teste padrão disponível."
              items={presets}
              onEditPolicy={isAdmin ? (a) => setEditing(a) : undefined}
            />
            <AssessmentsSection
              heading="Personalizados"
              description="Testes criados para a empresa. Nunca são reaproveitados entre vagas."
              emptyMessage="Nenhum teste personalizado cadastrado."
              items={adHoc}
            />
          </div>
        )}
      </div>

      <AssessmentPolicyDialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open && !patchAssessment.isPending) setEditing(null);
        }}
        assessment={editing}
        isSaving={patchAssessment.isPending}
        onSave={(values) => void handleSavePolicy(values)}
      />
    </AdminLayout>
  );
}

interface AssessmentsSectionProps {
  heading: string;
  description: string;
  emptyMessage: string;
  items: AssessmentListEntry[];
  /** Presente só na seção de presets E só para admin — ad-hoc/leitura não editam. */
  onEditPolicy?: (assessment: AssessmentListEntry) => void;
}

function AssessmentsSection({
  heading,
  description,
  emptyMessage,
  items,
  onEditPolicy,
}: AssessmentsSectionProps) {
  return (
    <section aria-label={`Testes — ${heading}`} className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-medium tracking-tight text-foreground">{heading}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {items.length === 0 ? (
        <p className="rounded-md border border-border bg-card px-4 py-6 text-sm text-muted-foreground">
          {emptyMessage}
        </p>
      ) : (
        <div className="rounded-md border border-border">
          <Table className="table-fixed" containerClassName="overflow-auto">
            <TableHeader className="border-b border-border bg-card">
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Reaproveitamento</TableHead>
                {onEditPolicy ? <TableHead aria-label="Ações" className="w-[160px]" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((assessment) => (
                <TableRow key={assessment.id} data-testid={`assessment-row-${assessment.id}`}>
                  <TableCell className="text-sm font-medium text-foreground">
                    {assessment.name}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {assessment.description ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {policyLabel(assessment)}
                  </TableCell>
                  {onEditPolicy ? (
                    <TableCell className="text-right">
                      <button
                        type="button"
                        onClick={() => onEditPolicy(assessment)}
                        className="rounded-sm text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        data-testid={`assessment-edit-policy-${assessment.id}`}
                      >
                        Editar política
                      </button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

function StatusPanel({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-md border border-border bg-card px-6 py-16 text-center">
      <div className="flex size-16 items-center justify-center rounded-full bg-muted text-foreground">
        {icon}
      </div>
      <div className="flex flex-col items-center gap-2">
        <p className="text-xl font-medium tracking-tight text-foreground">{title}</p>
        {description ? <p className="text-base text-muted-foreground">{description}</p> : null}
      </div>
    </div>
  );
}
